/* ExecutionHistory — List of automation executions + detail drill-down */
import { useState } from 'react';
import css from '../AutomationEditor.module.css';
import { useAutomationExecutions, useExecutionDetail } from '../../../hooks/useAutomations';
import type { AutomationExecution } from '../../../types/automations';

interface Props {
  automationId: number;
}

const STATUS_COLORS: Record<string, string> = {
  completed: '#22c55e',
  running: '#3b82f6',
  failed: '#ef4444',
  skipped: '#94a3b8',
};

function formatDate(dateStr: string) {
  return new Date(dateStr).toLocaleString();
}

function formatDuration(start: string, end?: string) {
  if (!end) return 'In progress';
  const ms = new Date(end).getTime() - new Date(start).getTime();
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

export default function ExecutionHistory({ automationId }: Props) {
  const { data: executions = [], isLoading } = useAutomationExecutions(automationId);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const { data: detail } = useExecutionDetail(automationId, selectedId);

  if (isLoading) {
    return <div className={css.historyLoading}>Loading execution history...</div>;
  }

  if (executions.length === 0) {
    return (
      <div className={css.historyEmpty}>
        <div className={css.historyEmptyIcon}>📋</div>
        <div>No executions yet</div>
        <div className={css.historyEmptyHint}>
          Executions will appear here when automations are triggered by stage changes.
        </div>
      </div>
    );
  }

  return (
    <div className={css.historyLayout}>
      {/* Execution list */}
      <div className={css.historyList}>
        <div className={css.historyListHeader}>Executions ({executions.length})</div>
        {executions.map((exec) => (
          <ExecutionRow
            key={exec.id}
            execution={exec}
            isSelected={exec.id === selectedId}
            onClick={() => setSelectedId(exec.id === selectedId ? null : exec.id)}
          />
        ))}
      </div>

      {/* Detail panel */}
      {selectedId && detail && (
        <div className={css.historyDetail}>
          <div className={css.historyDetailHeader}>
            <span
              className={css.historyStatusDot}
              style={{ background: STATUS_COLORS[detail.status] }}
            />
            Execution #{detail.id}
            <span className={css.historyDetailStatus}>{detail.status}</span>
          </div>

          <div className={css.historyDetailMeta}>
            <div>
              <strong>Started:</strong> {formatDate(detail.started_at)}
            </div>
            {detail.completed_at && (
              <div>
                <strong>Duration:</strong> {formatDuration(detail.started_at, detail.completed_at)}
              </div>
            )}
            {detail.error_message && <div className={css.historyError}>{detail.error_message}</div>}
          </div>

          {detail.trigger_data && (
            <div className={css.historySection}>
              <div className={css.historySectionTitle}>Trigger Data</div>
              <pre className={css.historyJson}>{JSON.stringify(detail.trigger_data, null, 2)}</pre>
            </div>
          )}

          <div className={css.historySection}>
            <div className={css.historySectionTitle}>Steps ({detail.steps?.length || 0})</div>
            {detail.steps?.map((step) => (
              <div key={step.id} className={css.historyStep}>
                <div className={css.historyStepHeader}>
                  <span
                    className={css.historyStatusDot}
                    style={{ background: STATUS_COLORS[step.status] }}
                  />
                  <span className={css.historyStepName}>
                    {step.node_label || step.node_def_name}
                  </span>
                  <span className={css.historyStepType}>{step.node_def_key}</span>
                </div>
                {step.error_message && <div className={css.historyError}>{step.error_message}</div>}
                {step.output_data && (
                  <pre className={css.historyJson}>{JSON.stringify(step.output_data, null, 2)}</pre>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ExecutionRow({
  execution,
  isSelected,
  onClick,
}: {
  execution: AutomationExecution;
  isSelected: boolean;
  onClick: () => void;
}) {
  const caseRef = (execution.trigger_data as Record<string, unknown>)?.case_ref as string;

  return (
    <div
      className={`${css.historyRow} ${isSelected ? css.historyRowSelected : ''}`}
      onClick={onClick}
    >
      <span
        className={css.historyStatusDot}
        style={{ background: STATUS_COLORS[execution.status] }}
      />
      <div className={css.historyRowBody}>
        <div className={css.historyRowTop}>
          <span className={css.historyRowStatus}>{execution.status}</span>
          {caseRef && <span className={css.historyRowCase}>{caseRef}</span>}
        </div>
        <div className={css.historyRowTime}>
          {formatDate(execution.started_at)}
          {execution.completed_at &&
            ` • ${formatDuration(execution.started_at, execution.completed_at)}`}
        </div>
      </div>
    </div>
  );
}
